import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, Pin, Trash2 } from "lucide-react";
import { getAllNotices, pinNotice, deleteNotice } from "../../api/noticeApi";
import { Badge, IconButton } from "../ui";
import WidgetCard from "./WidgetCard";
import { useToast } from "../../context/ToastContext";

const CATEGORY_BADGE_VARIANTS = {
  GENERAL: "default",
  EXAM: "primary",
  HOLIDAY: "danger",
  EVENT: "purple",
  FEE: "warning",
  URGENT: "danger",
};

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
}

export default function NoticeBoardWidget({ limit = 5 }) {
  const navigate = useNavigate();

  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllNotices({});
      const list = Array.isArray(res) ? res : res.notices || [];

      const sorted = [...list].sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setNotices(sorted.slice(0, limit));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load notices");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePin = async (id) => {
    setActingId(id);
    try {
      await pinNotice(id);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update pin status");
    } finally {
      setActingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this notice?")) return;
    setActingId(id);
    try {
      await deleteNotice(id);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete notice");
    } finally {
      setActingId(null);
    }
  };

  return (
    <WidgetCard
      title="Notice Board"
      icon={Megaphone}
      loading={loading}
      empty={!loading && notices.length === 0}
      emptyText="No notices published yet."
      onViewAll={() => navigate("/notices")}
    >
      

      <div className="space-y-2">
        {notices.map((notice) => (
          <div
            key={notice._id}
            className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 rounded-xl border p-3 transition-all hover:shadow-xs ${
              notice.isPinned
                ? "border-amber-200 bg-amber-50/40"
                : "border-neutral-200 dark:border-neutral-800/80 bg-gradient-to-r from-slate-50/50 to-white hover:border-indigo-200"
            }`}
          >
            <div className="min-w-0 flex-1">
              <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                <Badge
                  variant={CATEGORY_BADGE_VARIANTS[notice.category] || "default"}
                  className="text-[10px] px-2 py-0.5 font-extrabold uppercase tracking-wider"
                >
                  {notice.category}
                </Badge>
                {notice.isPinned && (
                  <Badge variant="warning" className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 font-extrabold">
                    <Pin size={10} className="fill-current text-amber-700 shrink-0" />
                    Pinned
                  </Badge>
                )}
              </div>
              <p className="truncate text-xs font-bold text-slate-800 dark:text-neutral-100 leading-snug">
                {notice.title}
              </p>
              <p className="mt-0.5 text-[11px] font-semibold text-slate-400 dark:text-neutral-500">
                {formatDate(notice.createdAt)}
              </p>
            </div>

            <div className="flex shrink-0 items-center justify-end gap-1 pt-2 border-t border-slate-100 dark:border-neutral-800 sm:border-0 sm:pt-0">
              <IconButton
                type="button"
                variant={notice.isPinned ? "warning" : "ghost"}
                size="sm"
                disabled={actingId === notice._id}
                onClick={() => handlePin(notice._id)}
                title={notice.isPinned ? "Unpin" : "Pin"}
                className={`rounded-lg transition-colors ${ notice.isPinned ? "bg-amber-100 text-amber-800 hover:bg-amber-200" : "text-slate-500 dark:text-neutral-400 hover:bg-neutral-100 dark:bg-neutral-800 dark:hover:bg-neutral-800" }`}
              >
                <Pin size={14} />
              </IconButton>
              <IconButton
                type="button"
                variant="ghost"
                size="sm"
                disabled={actingId === notice._id}
                onClick={() => handleDelete(notice._id)}
                title="Delete"
                className="rounded-lg text-rose-600 hover:bg-rose-50 hover:text-rose-700 transition-colors"
              >
                <Trash2 size={14} />
              </IconButton>
            </div>
          </div>
        ))}
      </div>
    </WidgetCard>
  );
}